/**
 * Stripe Terminal Service — in-person M2 reader charging via the NATIVE WKWebView bridge.
 *
 * ARCHITECTURE (corrected 2026-08-30): SLIK's iOS app is a SwiftUI + WKWebView shell
 * (NOT Capacitor). The Bluetooth M2 reader is driven by the native Stripe Terminal SDK,
 * exposed to this web POS through `TerminalBridge.swift` on `window.webkit.messageHandlers
 * .slikTerminal`. We post a message per action and the native side answers via a global
 * `window.__slikTerminalEvent(requestId, {ok, ...})`. This file is that JS half.
 *
 * FLOW: chargeOnReader() → createTerminalPaymentIntent (server, card_present PI) → native
 * discover → connect (needs a Stripe Location id) → collect + confirm on the reader.
 * A paid card_present sale spiderwebs downstream exactly like a link/cash sale (webhook +
 * receipt + refundable). POS-native only; web build shows nothing (isTerminalAvailable=false).
 *
 * BEHAVIOR-PIN: project-slik-domain-unify-tollfree-2026-08-30 (M2 native bridge, Path A)
 */

import { capacitorNative, capDiscover, capConnect, capCollect, capCancel, setCapStatus, capIsConnected } from './terminalCapacitor';
import { logTerminalEvent } from './diagnosticsService';

// ENVIRONMENT-AWARE (2026-09-22): was hardcoded to prod, so the STAGING staff web app called
// PROD terminal functions. Derive from the build's Firebase project → staging build hits staging
// (test-mode) functions, prod build is unchanged. Stripe test-vs-live mode is set entirely by which
// project's connectionToken function we hit (staging = test keys) — NOT by any client flag.
const PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'slik-pos-barberlegacy';
const FUNCTIONS_URL = `https://us-central1-${PROJECT_ID}.cloudfunctions.net`;

// ALWAYS-ON REGISTER (Jose 2026-09-22): every barber's phone with the app is a working register,
// same as the shop iPads — no enable toggle. (We started with an off-by-default backup toggle, then
// Jose chose always-on so a barber's experience is identical on iPad or phone, iOS or Android.)
const READER_SIM_KEY = 'slik_reader_simulated'; // test-only: use Stripe's simulated reader (no hardware)
// READER-TO-STATION PAIRING (Jose 2026-09-22, 5 M2 readers): a Bluetooth reader pairs to ONE device,
// and with several readers broadcasting in one shop, connecting to "the first one discovered" would
// cross-wire stations. Each device stores the serial of ITS assigned reader; checkout connects to
// that serial specifically (Square-style "this reader belongs to this iPad"). Per-device (localStorage).
const READER_SERIAL_KEY = 'slik_reader_serial';
export const getAssignedReaderSerial = (): string => { try { return localStorage.getItem(READER_SERIAL_KEY) || ''; } catch { return ''; } };
export const setAssignedReaderSerial = (serial: string): void => { try { if (serial) localStorage.setItem(READER_SERIAL_KEY, serial); else localStorage.removeItem(READER_SERIAL_KEY); } catch { /* */ } };
// HARD PROD GUARD (Jose "this is money" 2026-09-22): the simulated reader can NEVER be active in
// production, no matter what localStorage says. The UI only exposes this toggle off-prod, and
// origins are already separate, but this makes a fake-reader charge impossible in prod by construction.
const IS_PROD = (process.env.REACT_APP_FIREBASE_PROJECT_ID || 'slik-pos-barberlegacy') === 'slik-pos-barberlegacy';
const readerSimulated = (): boolean => { if (IS_PROD) return false; try { return localStorage.getItem(READER_SIM_KEY) === 'true'; } catch { return false; } };
export const setReaderSimulated = (on: boolean): void => { try { localStorage.setItem(READER_SIM_KEY, on ? 'true' : 'false'); } catch { /* */ } };
export const isReaderSimulated = (): boolean => readerSimulated();

// ── Native bridge plumbing ────────────────────────────────────────────────────────────
type BridgeResult = { ok: boolean; error?: string; [k: string]: any };
const pending = new Map<string, (r: BridgeResult) => void>();
let reqSeq = 0;

// Native calls this to resolve a request. Install once.
function installEventSink() {
  if ((window as any).__slikTerminalEvent) return;
  (window as any).__slikTerminalEvent = (requestId: string, result: BridgeResult) => {
    const resolve = pending.get(requestId);
    if (resolve) { pending.delete(requestId); resolve(result || { ok: false, error: 'empty result' }); }
  };
}

function bridge(): any {
  return (window as any).webkit?.messageHandlers?.slikTerminal;
}

function call(action: string, args: Record<string, any> = {}, timeoutMs = 120000): Promise<BridgeResult> {
  installEventSink();
  const mh = bridge();
  if (!mh) return Promise.resolve({ ok: false, error: 'Terminal bridge not available (web/non-native)' });
  const requestId = `t${Date.now()}_${reqSeq++}`;
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      if (pending.has(requestId)) { pending.delete(requestId); resolve({ ok: false, error: 'Reader timed out' }); }
    }, timeoutMs);
    pending.set(requestId, (r) => { clearTimeout(timer); resolve(r); });
    try { mh.postMessage({ action, requestId, ...args }); }
    catch (e: any) { clearTimeout(timer); pending.delete(requestId); resolve({ ok: false, error: e?.message || 'postMessage failed' }); }
  });
}

// ── Public API (interface preserved for existing consumers) ───────────────────────────

/**
 * Can this device charge on a reader RIGHT NOW? Any native shell is a register (always-on):
 *  • SwiftUI iPad (WKWebView slikTerminal bridge present) → yes.
 *  • Capacitor app (iOS/Android incl. barber phones) → yes.
 *  • Plain web (Safari) → no. Gate all reader UI on this.
 */
export const isTerminalAvailable = (): boolean => !!bridge() || capacitorNative();

/** Token provider — still real + independently testable (POST returns { secret }). Native SDK also uses it. */
export const fetchConnectionToken = async (): Promise<string> => {
  const res = await fetch(`${FUNCTIONS_URL}/connectionToken`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
  });
  if (!res.ok) throw new Error(`Connection token request failed (${res.status})`);
  const data = await res.json();
  if (!data?.secret) throw new Error('No connection token secret returned');
  return data.secret as string;
};

export interface TerminalReader { id: string; label: string; batteryLevel?: number; serialNumber?: string; }
export interface TerminalChargeResult { success: boolean; paymentIntentId?: string; error?: string; }

/**
 * After a reader charge FAILS, pull the AUTHORITATIVE decline reason from Stripe (via the PaymentIntent's
 * last_payment_error) so the diagnostics log shows "amex · card_not_supported" instead of the generic
 * "card couldn't be processed". Best-effort + short timeout; a lookup failure never affects checkout.
 * (Jose 2026-10-30 — "capture the real Stripe decline code so next time we see why Amex failed")
 */
async function fetchDeclineInfo(paymentIntentId?: string): Promise<{ status?: string; code?: string; declineCode?: string; failureCode?: string; failureMessage?: string; errorType?: string; cardBrand?: string; last4?: string; message?: string } | null> {
  if (!paymentIntentId) return null;
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 6000);
    const res = await fetch(`${FUNCTIONS_URL}/terminalPaymentIntentInfo`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentIntentId }), signal: ctrl.signal,
    });
    clearTimeout(timer);
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

/** The Stripe Location id an M2 reader is registered to (required to connect). Server picks/creates one. */
export const getTerminalLocationId = async (): Promise<string> => {
  const res = await fetch(`${FUNCTIONS_URL}/terminalLocation`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
  });
  if (!res.ok) throw new Error(`Could not load Stripe Location (${res.status})`);
  const data = await res.json();
  if (!data?.locationId) throw new Error('No Stripe Location found — register your reader to a Location in Stripe.');
  return data.locationId as string;
};

/** Abort any in-flight reader discovery/collect (Jose 2026-08-30): hitting Cancel must stop the
 *  native BLE scan immediately, not let it run until the 2-min timeout. Best-effort. */
export const cancelReaderOperation = async (): Promise<void> => {
  try { if (capacitorNative()) await capCancel(); else await call('cancel', {}, 8000); }
  catch { /* best effort — closing the modal anyway */ }
};

/** Discover nearby M2 (Bluetooth) readers — via the Capacitor plugin or the WKWebView bridge.
 *  locationId MUST be supplied for the Capacitor path: the community plugin caches it at DISCOVER
 *  time (self.locationId) and force-unwraps it at connect (`locationId: self.locationId!`), so
 *  omitting it here crashes natively the moment connect starts. (Fix 2026-09-22) */
export const discoverReaders = async (timeoutMs = 120000, locationId?: string): Promise<TerminalReader[]> => {
  // `simulated` (the device toggle) alone decides virtual-reader vs real-M2. It is DECOUPLED from
  // Stripe test/live mode: that's set server-side by which connectionToken function we hit (staging =
  // test keys). So on staging you can flip simulated OFF and pair the REAL M2, still charging test $.
  const r = capacitorNative()
    ? await capDiscover(fetchConnectionToken, { simulated: readerSimulated(), timeoutMs, locationId })
    : await call('discover', {}, timeoutMs);
  if (!r.ok) throw new Error(r.error || 'Reader discovery failed');
  return (r.readers || []).map((x: any) => ({
    id: x.serial, serialNumber: x.serial, label: x.label || x.serial, batteryLevel: x.battery,
  }));
};

/** Connect to a reader (by serial) at a Stripe Location. */
export const connectReader = async (serial: string, locationId: string): Promise<TerminalReader> => {
  const r = await call('connect', { serial, locationId });
  if (!r.ok) throw new Error(r.error || 'Reader connect failed');
  return { id: r.serial || serial, serialNumber: r.serial || serial, label: r.serial || serial };
};

/** Do we currently hold a live connection to the reader? (Capacitor only — the WKWebView bridge
 *  manages its own connection and isn't queried here, so it always re-connects at charge time.) */
export const isReaderConnected = (serial?: string): boolean => capacitorNative() && capIsConnected(serial || getAssignedReaderSerial() || undefined);

/**
 * WARM-CONNECT the reader WITHOUT taking a payment (Jose 2026-10-23). Jose: "look for the reader once
 * the app comes on and sync to it" — so the reader is ready and the first charge doesn't have to run
 * discover+connect from cold (and so resetting the app can re-pair without starting a fake sale).
 * Discover → connect to THIS station's assigned reader (or the first found). Best-effort + silent:
 * returns {connected:false,...} on any problem, never throws. Safe to fire-and-forget on app open.
 * Once connected, collectCardPayment() skips straight to collect (capIsConnected fast-path).
 */
export const warmConnectReader = async (onStatus?: (m: string) => void): Promise<{ connected: boolean; serial?: string; error?: string }> => {
  if (!isTerminalAvailable()) return { connected: false, error: 'not-native' };
  const status = (m: string) => { try { onStatus?.(m); } catch { /* noop */ } };
  const assigned = getAssignedReaderSerial();
  if (capacitorNative() && capIsConnected(assigned || undefined)) return { connected: true, serial: assigned || undefined }; // already warm
  if (capacitorNative()) setCapStatus(status);
  try {
    let locationId = '';
    try { locationId = await getTerminalLocationId(); } catch { if (capacitorNative()) return { connected: false, error: 'no-location' }; }
    status('Connecting to the reader…');
    let readers: TerminalReader[] = [];
    try { readers = await discoverReaders(20000, locationId); } catch (e: any) { return { connected: false, error: e?.message || 'discover failed' }; }
    if (!readers.length) return { connected: false, error: 'no-readers' };
    let target = readers[0];
    if (assigned) { const m = readers.find((r) => (r.serialNumber || r.id) === assigned); if (m) target = m; }
    const serial = target.serialNumber || target.id;
    const connected = capacitorNative() ? await capConnect(serial, locationId, readerSimulated()) : await call('connect', { serial, locationId }, 600000);
    if (!connected.ok) { logTerminalEvent({ level: 'error', event: 'warm_connect_failed', message: connected.error || 'connect failed', readerSerial: serial }); return { connected: false, error: connected.error }; }
    logTerminalEvent({ level: 'info', event: 'warm_connect_ok', readerSerial: serial });
    return { connected: true, serial };
  } catch (e: any) {
    return { connected: false, error: e?.message || 'warm connect failed' };
  } finally {
    if (capacitorNative()) setCapStatus(null);
  }
};

/**
 * FULL in-person charge orchestration: create the card_present PaymentIntent, then
 * discover → connect → collect on the reader. Returns the PI id so the sale completes
 * and becomes refundable — identical downstream to a link/cash sale.
 */
// DOUBLE-TAP GUARD (Jose 2026-10-29): a second "Card" tap while the reader is already collecting threw
// "Could not execute collectPaymentMethod because the SDK is busy with another command" — the charge still
// went through, but a cryptic error flashed. This one in-flight lock makes a duplicate collect a clean
// no-op (never re-invokes the reader, never risks a double charge) while one is already running.
let _collectInFlight = false;

export const collectCardPayment = async (
  amountDollars: number,
  opts?: { appointmentId?: string; clientEmail?: string; description?: string; onStatus?: (msg: string) => void }
): Promise<TerminalChargeResult> => {
  if (!isTerminalAvailable()) return { success: false, error: 'In-person reader is only available in the native app.' };
  if (_collectInFlight) {
    logTerminalEvent({ level: 'warn', event: 'collect_double_tap_ignored', message: 'duplicate collect ignored — one already running', amount: amountDollars });
    return { success: false, error: 'A card charge is already running on the reader — give it a moment.' };
  }
  _collectInFlight = true;
  const status = (m: string) => { try { opts?.onStatus?.(m); } catch { /* noop */ } };
  // Route the Capacitor plugin's async firmware-update progress into the same status line, so a
  // multi-minute first-connect update reads as "Updating reader… 47%", not a frozen spinner.
  if (capacitorNative()) setCapStatus(status);
  try {
    // 1) server: card_present PaymentIntent. Keep connect-phase copy free of the words "charge/process/
    // tap" so the client overlay never shows "Processing" or a tap prompt before the reader is ready
    // (Jose 2026-10-22: "Processing" made clients try to tap too early). This is the LOOKING phase.
    status('Looking for the reader — please wait…');
    const piRes = await fetch(`${FUNCTIONS_URL}/createTerminalPaymentIntent`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        amount: Math.round(amountDollars * 100),
        currency: 'usd',
        description: opts?.description || 'SLIK Barber Lounge — in-person',
        receipt_email: opts?.clientEmail,
        metadata: { appointmentId: opts?.appointmentId || '' },
      }),
    });
    const pi = await piRes.json();
    if (!piRes.ok || !pi?.clientSecret) {
      logTerminalEvent({ level: 'error', event: 'pi_failed', message: pi?.error || `PI create ${piRes.status}`, amount: amountDollars });
      return { success: false, error: pi?.error || 'Could not start the charge' };
    }

    // 2) ensure a reader is connected (discover → connect at our Location). FAST PATH (Jose 2026-10-23):
    // if it's already warm-connected (app-open warm connect or a prior charge) skip discover+connect
    // entirely and go straight to collect — no "Looking for the reader" delay. Otherwise connect cold.
    const assignedSerial = getAssignedReaderSerial();
    let serial = assignedSerial;
    if (capacitorNative() && capIsConnected(assignedSerial || undefined)) {
      // Warm connection present — nothing to discover/connect.
    } else {
      // Location id FIRST: the Capacitor plugin caches it at discover time and force-unwraps it at
      // connect, so it must be passed into discovery (not just connect). Harmless for the WKWebView
      // path (that gets its own locationId at connect).
      let locationId = '';
      try { locationId = await getTerminalLocationId(); } catch (e: any) {
        if (capacitorNative()) return { success: false, error: `Reader error (location): ${e?.message || 'no Stripe Location'}` };
      }

      // 30s cap: a fully-off M2 isn't broadcasting, so scanning longer is pointless — fail fast with a
      // clear "press the button".
      status('Looking for your reader…');
      let readers: TerminalReader[] = [];
      let discoverErr = '';
      try { readers = await discoverReaders(30000, locationId); } catch (e: any) { readers = []; discoverErr = e?.message || ''; }
      if (!readers.length) {
        // On the Capacitor path surface the REAL error (init/token/plugin), so a failure isn't
        // masked as "reader off". The friendly message stays for the real-M2 iPad (nothing found).
        logTerminalEvent({ level: 'error', event: 'reader_not_found', message: discoverErr || 'no readers discovered', amount: amountDollars });
        return { success: false, error: (capacitorNative() && discoverErr) ? `Reader error: ${discoverErr}` : "Reader is off — press the M2's power button once, then tap Card again." };
      }
      status('Connecting to the reader… (first time may update it — can take a few minutes)');
      // connect timeout is generous — the FIRST connect to a reader can trigger a firmware update.
      // Pick THIS station's assigned reader by serial (5-reader shops). Only fall back to first-found
      // when no reader is paired to this device yet (single-reader shops / pre-pairing).
      let target = readers[0];
      if (assignedSerial) {
        const match = readers.find((r) => (r.serialNumber || r.id) === assignedSerial);
        if (!match) {
          logTerminalEvent({ level: 'error', event: 'assigned_reader_missing', message: `assigned …${assignedSerial.slice(-4)} not among ${readers.length} found`, readerSerial: assignedSerial });
          return { success: false, error: `This station's reader (…${assignedSerial.slice(-4)}) wasn't found. Power it on and keep it close, or re-pair it in Settings → Payments.` };
        }
        target = match;
      }
      serial = target.serialNumber || target.id;
      const connected = capacitorNative()
        ? await capConnect(serial, locationId, readerSimulated())
        : await call('connect', { serial, locationId }, 600000);
      if (!connected.ok) {
        logTerminalEvent({ level: 'error', event: 'connect_failed', message: connected.error || 'connect failed', readerSerial: serial });
        return { success: false, error: connected.error || 'Reader connect failed' };
      }
    }

    // 3) collect + confirm on the reader
    status('Ready — tap, insert, or swipe the card on the reader.');
    const collected = capacitorNative()
      ? await capCollect(pi.clientSecret, pi.paymentIntentId)
      : await call('collect', { clientSecret: pi.clientSecret }, 180000);
    if (!collected.ok) {
      // GHOST-SUCCESS GUARD (Jose 2026-10-30): a network error can lose the "succeeded" response. Before we
      // declare failure, ASK Stripe whether the charge actually went through — reporting failure on a charge
      // that SUCCEEDED is exactly how a client gets double-charged on the re-tap. This same lookup also
      // returns the REAL decline/failure reason (brand + code) for the diagnostics log.
      const info = await fetchDeclineInfo(pi.paymentIntentId);
      if (info?.status === 'succeeded') {
        logTerminalEvent({ level: 'info', event: 'charge_success', readerSerial: serial, amount: amountDollars });
        return { success: true, paymentIntentId: pi.paymentIntentId };
      }
      const reason = info?.declineCode || info?.code || info?.failureCode || info?.errorType || '';
      const brand = info?.cardBrand ? info.cardBrand.toUpperCase() : '';
      const detail = info ? `${brand ? brand + ' · ' : ''}${reason || 'failed'}${info.message ? ` — ${info.message}` : ''}`.trim() : '';
      logTerminalEvent({
        level: 'error', event: 'charge_failed',
        message: detail || collected.error || 'payment not completed',
        readerSerial: serial, amount: amountDollars,
        code: info?.code, declineCode: info?.declineCode || info?.failureCode, cardBrand: info?.cardBrand, last4: info?.last4,
      });
      // Accurate guidance by failure type. A TRUE connection failure → "tap again". A card/processing
      // error (e.g. Amex `processing_error` — the brand isn't going through) → "try another card" so the
      // barber stops fighting the same card. Everything else → the raw reason.
      const blob = `${info?.errorType || ''} ${info?.declineCode || info?.code || ''} ${info?.message || ''} ${collected.error || ''}`.toLowerCase();
      const isConn = /api_connection|unable to connect|no internet|connection (lost|failed|error|was lost)|network error|timed ?out|timeout|unreachable/.test(blob);
      const isProcessing = /processing_error|couldn.?t be processed/.test(blob);
      const userErr = isConn
        ? 'Network hiccup during the charge — check the connection and tap the card again.'
        : isProcessing
          ? `This card couldn’t be processed${info?.cardBrand ? ` (${info.cardBrand.toUpperCase()})` : ''} — please try another card.`
          : (collected.error || 'Payment not completed');
      return { success: false, error: userErr };
    }
    logTerminalEvent({ level: 'info', event: 'charge_success', readerSerial: serial, amount: amountDollars });
    return { success: true, paymentIntentId: collected.paymentIntentId || pi.paymentIntentId };
  } catch (e: any) {
    logTerminalEvent({ level: 'error', event: 'charge_exception', message: e?.message || 'reader charge failed', amount: amountDollars });
    return { success: false, error: e?.message || 'Reader charge failed' };
  } finally {
    _collectInFlight = false;                   // release the double-tap lock for the next charge
    if (capacitorNative()) setCapStatus(null); // detach the status sink for the next charge
  }
};
